import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";

// Services
import { packageApi } from "@/main/api";
import { getTranslateByKey } from "@/main/services/utils";
import { ChangeImportanceOrderRequest, PackageTypeChangeImportance } from "@/main/models/request";

// Types
import { KeyValue } from "@/main/models/common";
import { PackageTypeResponse } from "@/main/models/response";

// Components
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import Button from "@/main/components/shared/button/button";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import Box from "@/main/components/shared/box";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import KogModalFadeComponent from "@/main/components/modal/ModalFadeComponent";
import VerticalLineWithArrows from "@/main/components/shared/verticalLineWithArrows/verticalLineWithArrows";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ModalConfirm from "@/main/components/shared/modalConfirm/modalConfirm";

// Components/Styles Local
import ListItemDragAndDrop from "@/main/screens/registration/packWorkflow/components/ListItemDragAndDrop/listItemDragAndDrop";
import ModalPackWorkflow from "./components/modalPackWorkflow/modalPackWorkflow";

import * as S from "./styles";
import { displayMessage } from "@/main/services/utilsResponsive";
import { KOGAreaButtons } from "@/main/components/shared/styles";

const INITIAL_FORM_STATE = {
	country: null as KeyValue | null,
};
const INITIAL_PACKAGE_TYPE_EDIT = {
	guidEdit: null as string | null,
	guidCountryEdit: null as string | null,
};

export interface IHandleAddCategoryToGrid {
	data: PackageTypeResponse;
	guidPackageType?: string | null;
	isDelete?: boolean;
}

type IPackageTypeEdit = typeof INITIAL_PACKAGE_TYPE_EDIT;
type ISearchAndAddPackWorkflowForm = typeof INITIAL_FORM_STATE;

function PackWorkflow() {
	const dispatch = useDispatch();

	// Reducers

	const countryReducer = useCountryReducer();

	const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
	const [isOpenModalFade, setIsOpenModalFade] = useState<boolean>(false);
	const [isLoadingSearch, setIsLoadingSearch] = useState<boolean>(false);
	const [selectPackageTypeForDelete, setSelectPackageTypeForDelete] = useState<string | null>(null);
	const [selectPackageTypeForEdit, setSelectPackageTypeForEdit] = useState<IPackageTypeEdit>(INITIAL_PACKAGE_TYPE_EDIT);
	const [packageTypeData, setPackageTypeData] = useState<PackageTypeResponse[] | null>(null);

	const optionDefaultCountryDrop =
		countryReducer.countriesUser.data !== null && countryReducer.countriesUser.data.length === 1
			? countryReducer.countriesUser.data[0]
			: null;

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors },
		reset,
		setError,
		watch,
		getValues,
	} = useForm<ISearchAndAddPackWorkflowForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const countryWatcher = watch("country");

	function handleClear() {
		if (!optionDefaultCountryDrop) reset(INITIAL_FORM_STATE);

		setPackageTypeData(null);
	}

	function handleOpenModal() {
		if (!countryWatcher) {
			setError("country", {
				type: "required",
				message: getTranslateByKey("information.countryRequired", true),
			});
		} else {
			setIsOpenModal(true);
		}
	}

	function handleButtonDeletar(guidPackageType: string) {
		setSelectPackageTypeForDelete(guidPackageType);
		setIsOpenModalFade(true);
	}

	function handleButtonEdit(guidPackageType: string, guidCountry: string) {
		setSelectPackageTypeForEdit({
			guidEdit: guidPackageType,
			guidCountryEdit: guidCountry,
		});
		setIsOpenModal(true);
	}

	function handleModalConfirm() {
		if (selectPackageTypeForDelete)
			packageApi
				.delete(selectPackageTypeForDelete)
				.then((res) => {
					const resData = res.data.result;
					handleAddCategoryToGrid({
						data: resData,
						guidPackageType: selectPackageTypeForDelete,
						isDelete: true,
					});
					toast.success(getTranslateByKey("information.categoryDeletedSuccessfully"));
				})
				.catch(() => {})
				.finally(() => {
					setIsOpenModalFade(false);
					setSelectPackageTypeForDelete(null);
				});
	}

	function handleSearch() {
		setPackageTypeData(null);
		setIsLoadingSearch(true);
		const guidCountry = String(getValues("country.key"));
		if (guidCountry) {
			packageApi
				.getAllByCoutryId({ guidCountry })
				.then((res) => {
					const resData = res.data.result;
					const sortedObjects = resData.sort((a, b) => a.importance - b.importance);
					setPackageTypeData(sortedObjects);
				})
				.catch(() => {})
				.finally(() => setIsLoadingSearch(false));
		} else {
			toast.error(getTranslateByKey("information.countryRequired", true));
		}
	}

	function handleSave() {
		const packageTypeChangeImportance: PackageTypeChangeImportance[] = [];
		if (packageTypeData) {
			packageTypeData.map((item) => {
				return packageTypeChangeImportance.push({
					guidPackageType: item.guid,
					importance: item.importance,
				});
			});
		}

		const request: ChangeImportanceOrderRequest = {
			guidCountry: selectPackageTypeForEdit.guidCountryEdit || String(getValues("country.key")),
			packageTypeChangeImportance,
		};

		packageApi
			.changeImportanceOrder(request)
			.then(() => {
				toast.success(getTranslateByKey("information.importanceSavedSuccessfully"));
			})
			.catch(() => {});
	}

	function handleAddCategoryToGrid({ data, guidPackageType = null, isDelete = false }: IHandleAddCategoryToGrid) {
		const dataPackageType = packageTypeData;
		const formattedData: PackageTypeResponse = {
			guid: data.guid,
			name: data.name,
			active: true,
			height: data.height,
			width: data.width,
			depth: data.depth,
			importance: data.importance,
			packageTypeExceptionResponse: data.packageTypeExceptionResponse,
		};
		if (guidPackageType && isDelete) {
			return setPackageTypeData((prevState) => {
				if (prevState) {
					const index = prevState.findIndex((item) => item.guid === guidPackageType);
					prevState.splice(index, 1);
					return [...prevState];
				}
				return null;
			});
		}
		let dataEdit: PackageTypeResponse | null = null;
		if (dataPackageType && guidPackageType) {
			dataEdit = dataPackageType.find((item) => item.guid === guidPackageType) || null;
		}
		if (dataEdit) {
			return setPackageTypeData((prev) => {
				let prevData = prev;
				if (prevData) {
					const newData = {
						guid: data.guid,
						name: data.name,
						height: data.height,
						width: data.width,
						depth: data.depth,
						active: data.active,
						importance: dataEdit?.importance,
						packageTypeExceptionResponse: data.packageTypeExceptionResponse,
					} as PackageTypeResponse;
					const index = prevData.findIndex((item) => item.guid === data.guid);
					if (index !== -1) {
						prevData[index] = newData;
					} else {
						prevData = [...prevData, newData];
					}
					return [...prevData];
				}
				return null;
			});
		}

		if (dataPackageType) {
			dataPackageType.push(formattedData);
			return setPackageTypeData(dataPackageType);
		}
		return null;
	}

	useEffect(() => {
		if (isOpenModalFade === false) {
			setSelectPackageTypeForDelete(null);
		}
	}, [isOpenModalFade]);

	useEffect(() => {
		if (optionDefaultCountryDrop) {
			handleSearch();
		}
	}, [optionDefaultCountryDrop]);

	useEffect(() => {
		dispatch(getCountriesUser());
	}, []);

	return (
		<>
			<ContainerScreen
				hasBackground
				title={`
      ${getTranslateByKey("information.registration")}
      ${" > "}
      ${getTranslateByKey("screen.packworkflow")}`}
			>
				<Box title={getTranslateByKey("information.filters")}>
					<form onSubmit={handleSubmit(handleSearch)} noValidate>
						<S.ContainerForm>
							<S.RowInput>
								<KogAutoCompleteControlled
									nameCustom="country"
									control={control}
									label={getTranslateByKey("information.country")}
									options={countryReducer.countriesUser.data}
									isLoading={countryReducer.countriesUser.isLoading}
									rulesCustom={{ required: true }}
									errorMessageCustom={
										errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
									}
									hasErrorCustom={!!errors.country}
									onChangeCustom={() => {}}
									valueDefaultKey={optionDefaultCountryDrop?.key}
									setValueCustom={setValue}
									disabled={optionDefaultCountryDrop !== null}
									id="id-packWorkflow-select-country"
								/>
							</S.RowInput>
							<KOGAreaButtons>
								<Button onClick={handleOpenModal} id="id-packWorkflow-btn-addCategory">
									{getTranslateByKey("information.addCategory")}
								</Button>
								<SecondaryButton type="submit" id="id-packWorkflow-btn-search" isLoading={isLoadingSearch}>
									{getTranslateByKey("information.search")}
								</SecondaryButton>
								<SecondaryButton onClick={handleClear} id="id-packWorkflow-btn-clear">
									{getTranslateByKey("information.clear")}
								</SecondaryButton>
							</KOGAreaButtons>
						</S.ContainerForm>
					</form>
				</Box>
				{isLoadingSearch && <LoadingComponent />}
				{packageTypeData && (
					<>
						<S.BoxPackWorkflow title={getTranslateByKey("screen.packworkflow")}>
							<S.TextInfo error={window.innerWidth < 1024}>
								<InfoIcon />{" "}
								{displayMessage(
									getTranslateByKey("information.thisFeatureIsAvailableOnlyOnDesktop"),
									getTranslateByKey("information.youCanDragAndDropThePositions"),
									1024,
								)}
							</S.TextInfo>
							<S.ContainerPackWorkflow>
								<S.BoxVerticalLineWithArrows>
									<span>{getTranslateByKey("information.importance")}</span>
									<VerticalLineWithArrows arrowUp />
								</S.BoxVerticalLineWithArrows>

								<S.BoxListItemDragAndDrop>
									<ListItemDragAndDrop
										packageTypeData={packageTypeData}
										setPackageTypeData={setPackageTypeData}
										buttonDelete={handleButtonDeletar}
										buttonEdit={handleButtonEdit}
									/>
								</S.BoxListItemDragAndDrop>
							</S.ContainerPackWorkflow>
						</S.BoxPackWorkflow>

						<S.AreaButton>
							<Button onClick={handleSave} id="id-packWorkflow-btn-save">
								{getTranslateByKey("information.save")}
							</Button>
						</S.AreaButton>
					</>
				)}
			</ContainerScreen>

			<KogModalFadeComponent open={isOpenModalFade} setOpen={setIsOpenModalFade}>
				<ModalConfirm
					titleModal={getTranslateByKey("information.areYouSureYouWantToDeleteRegister")}
					handleModalCancel={() => setIsOpenModalFade(false)}
					handleModalConfirm={handleModalConfirm}
				/>
			</KogModalFadeComponent>

			<KogModalSideComponent
				open={isOpenModal}
				closeModal={() => {
					setIsOpenModal(false);
					setSelectPackageTypeForEdit(INITIAL_PACKAGE_TYPE_EDIT);
				}}
			>
				{isOpenModal && (
					<ModalPackWorkflow
						guidCountry={selectPackageTypeForEdit.guidCountryEdit || String(getValues("country.key"))}
						setIsOpenModal={setIsOpenModal}
						selectPackageTypeForEdit={selectPackageTypeForEdit.guidEdit}
						handleAddCategoryToGrid={handleAddCategoryToGrid}
						packageTypeData={packageTypeData}
					/>
				)}
			</KogModalSideComponent>
		</>
	);
}

export default PackWorkflow;
